import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { AUTH } from "@/config";
import { supabase } from "@/lib/supabase";
import { authRedirectUrl, clearExistingSession, friendlyAuthError, resolvePostAuthRedirect } from "@/lib/auth";
import { useIsInAppBrowser } from "@/lib/in-app-browser";
import {
  AuthInput,
  AuthLabel,
  AuthShell,
  Divider,
  ErrorMessage,
  GoogleButton,
  InAppBrowserNotice,
  PasswordInput,
  PrimaryButton,
} from "@/components/auth/AuthShell";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const inAppBrowser = useIsInAppBrowser();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await clearExistingSession();
      const { error: signInError } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (signInError) return setError(friendlyAuthError(signInError.message));
      navigate({ to: await resolvePostAuthRedirect(), replace: true });
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogle() {
    setError(null);
    await clearExistingSession();
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: authRedirectUrl() },
    });
    if (oauthError) setError(friendlyAuthError(oauthError.message));
  }

  return (
    <AuthShell title="Sign in" subtitle="Welcome back to A1 Connect.">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <AuthLabel htmlFor="email">Email</AuthLabel>
          <AuthInput id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
        </div>
        <div>
          <AuthLabel htmlFor="password">Password</AuthLabel>
          <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
          <div className="mt-2 text-right">
            <Link to="/forgot-password" className="text-xs text-white/60 hover:text-white">
              Forgot password?
            </Link>
          </div>
        </div>
        <ErrorMessage message={error} />
        <PrimaryButton type="submit" disabled={submitting || !email || !password}>
          {submitting ? "Signing in…" : "Sign in"}
        </PrimaryButton>
      </form>

      <p className="mt-5 text-center text-sm text-white/70">
        New to A1?{" "}
        <Link to="/signup" className="font-semibold text-white underline decoration-a1-red underline-offset-4">
          Create a family account
        </Link>
      </p>

      {inAppBrowser && <InAppBrowserNotice />}
      {AUTH.googleEnabled && !inAppBrowser && (
        <>
          <Divider />
          <GoogleButton onClick={handleGoogle} label="Sign in with Google" />
        </>
      )}
    </AuthShell>
  );
}
